import React, { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPendingClaim } from './ClaimPage';
import { Button, Input, Card } from '../components';
import { loginAgent } from '../api';
import LottieSpinner from '../components/LottieSpinner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import Footer from '../components/Footer';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { agentData, setGlobalNotification } = useAuth();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Claim-flow: terug naar de claim-pagina (de CF keurt daar automatisch goed)
    const pending = getPendingClaim();
    if (pending && agentData !== undefined) {
      navigate(`/claim/${pending.listingId}`);
      return;
    }
    if (agentData?.isApproved) {
      navigate('/dashboard');
    } else if (agentData && !agentData.isApproved) {
      navigate('/pending-approval');
    }
  }, [agentData, navigate]);

  function getFriendlyError(error: any) {
    const code = error?.code;
    const key = code ? `firebase_errors.${code}` : 'firebase_errors.default';
    const translation = t(key);
    return translation === key ? t('firebase_errors.default') : translation;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await loginAgent(email, password);
      setGlobalNotification({ message: t('success_login'), type: 'success' });
      // AuthProvider will redirect based on agentData status
    } catch (err: any) {
      const friendly = getFriendlyError(err);
      setError(friendly);
      setGlobalNotification({ message: friendly, type: 'error' });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-neutral-100">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="flex-1 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <div className="p-8">
            <img src="/logo.png" alt="HomeMe Portaal Logo" className="h-20 w-auto mx-auto mb-6 rounded-[15px]" />
            <h2 className="text-2xl font-semibold text-center text-neutral-800 mb-2">{t('login')}</h2>
            <p className="text-sm text-center text-neutral-500 mb-6">{t('login_subtitle', 'Log in op je account om verder te gaan.')}</p>
            {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input label={t('email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
              <Input label={t('password')} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
              <Button type="submit" className="w-full" isLoading={loading} disabled={loading}>
                {t('login')}
              </Button>
            </form>
            <p className="text-sm text-center text-neutral-600 mt-6">
              {t('no_account')} <Link to="/register" className="font-medium text-homeme hover:underline">{t('register_here')}</Link>
            </p>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};
